import paramsBuilder from 'utils/paramsBuilder';
import getViewCheckpointId from 'utils/getViewCheckpointId';
import request from 'utils/request';
import { downloadFile } from 'containers/SurveySummaryStats/model';

export const EXPERIMENTAL_DESIGN_EXPORT_URL =
  '/api/v2/survey/export/experimental-design';

export const storeQuestionnaire = (params, options) => {
  const { surveyId, ...data } = params;
  const requestOptions = {
    ...options,
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  };
  return request(`/api/v3/survey-draft/${surveyId}`, requestOptions);
};

export const storeQuestionnaireTexts = (params, options) => {
  const { surveyId, ...data } = params;
  const requestOptions = {
    ...options,
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  };
  return request(
    `/api/v3/survey/${surveyId}/questionnaire/update`,
    requestOptions,
  );
};

export const storeDisqualificationRules = (params, options) => {
  const { surveyId, ...data } = params;
  const requestOptions = {
    ...options,
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  };
  return request(
    `/api/v3/survey/peeq/${surveyId}/disqualification-rules`,
    requestOptions,
  );
};

export function fetchQuestionnaire(params, options) {
  const requestOptions = {
    ...options,
    method: 'GET',
  };

  const urlParams = paramsBuilder({
    id: params.id,
    format: 'extended',
    checkpointId: getViewCheckpointId(),
  });

  return request(`/api/v1/survey${urlParams}`, requestOptions);
}

export function deleteMedia(params, options) {
  const { surveyId, refId, mediaType, side } = params;
  const requestOptions = {
    ...options,
    method: 'DELETE',
  };
  const sideQuery = side ? `&side=${encodeURIComponent(side)}` : '';
  return request(
    `/api/v3/media/draft-survey/${surveyId}?refId=${refId}&type=${mediaType}${sideQuery}`,
    requestOptions,
  );
}

export function fetchDemographics(params, options) {
  const { surveyId, isPeeq } = params;
  const requestOptions = {
    ...options,
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };
  let url = isPeeq
    ? `/api/v3/survey/peeq/${surveyId}/demographic/values`
    : `/api/v3/survey/${surveyId}/demographic/values`;

  const viewCheckpointId = getViewCheckpointId();
  if (viewCheckpointId) {
    url = `${url}?checkpointId=${viewCheckpointId}`;
  }

  return request(url, requestOptions);
}

export function fetchDesignOfExperiment(params, options) {
  const { surveyId, ...data } = params;
  const requestOptions = {
    ...options,
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  };
  return request(`/api/v3/survey-draft/${surveyId}/doe`, requestOptions);
}

export function fetchExperimentalDesign(surveyId, options) {
  const requestOptions = {
    ...options,
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  return request(
    `${EXPERIMENTAL_DESIGN_EXPORT_URL}?id=${surveyId}`,
    requestOptions,
  );
}

export async function downloadExperimentalDesign(surveyId, options) {
  const response = await downloadFile(
    surveyId,
    EXPERIMENTAL_DESIGN_EXPORT_URL,
    options,
  );

  if (response.blob?.type?.includes('application/json')) {
    const meta = JSON.parse(await response.blob.text());
    const url = meta?.url;

    if (!url) {
      throw new Error('Missing design download url');
    }

    return downloadFile(surveyId, url, options);
  }

  return response;
}

export function fetchDoeNumberOfChoices(params, options) {
  const { surveyId, ...data } = params;
  const requestOptions = {
    ...options,
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  };
  return request(
    `/api/v3/survey-draft/${surveyId}/numberOfChoices`,
    requestOptions,
  );
}

export function fetchLatestAiRevision(params, options) {
  const { surveyId } = params;
  const requestOptions = {
    ...options,
    method: 'GET',
  };
  return request(
    `/api/v3/survey-checkpoints/survey/${surveyId}/get-latest-ai-checkpoint`,
    requestOptions,
  );
}

export async function fetchDiff(params, options) {
  const { surveyId } = params;
  const requestOptions = {
    ...options,
    method: 'GET',
  };

  const { sourceCheckpointId, targetCheckpointId } = await request(
    `/api/v3/survey/${surveyId}/checkpoints-to-compare`,
    requestOptions,
  );

  if (!sourceCheckpointId || !targetCheckpointId) {
    return null;
  }

  return request(
    `/mastros/api/v1/survey/${surveyId}/checkpoint-diff?sourceCheckpointId=${sourceCheckpointId}&targetCheckpointId=${targetCheckpointId}`,
    {
      ...requestOptions,
    },
  );
}

export async function fetchNodeQuotasModel(params, options) {
  const { surveyId } = params;
  const requestOptions = {
    ...options,
    method: 'GET',
  };

  const urlParams = paramsBuilder({
    refId: params.questionRefId,
  });

  return request(
    `/api/v3/survey/${surveyId}/question-quota${urlParams}`,
    requestOptions,
  );
}
